import { useEffect, useMemo, useRef, useState } from "react";
import { useCustomClocks, searchCities, type ClockSpec } from "../hooks/useCustomClocks";
import { aqiBand as evidenceAqiBand } from "../lib/mayorBrief";
import type { PrecipNowcast } from "@chonburi/shared";
import { windDirLabel, uvBand, pulseColor, aqiBand, rainBadge, hmFromIso, timeInTz } from "../lib/worldStrip";

interface Props {
  hostAqi: number | null;
  hostAqiScale?: "thai-pcd" | "us-epa";
  hostAirKind?: "measured" | "model";
  hostAirDetail?: string;
  hostPm25: number | null;
  // Weather for the Chonburi host block — fetched in useWorldWeather.
  hostWeather: {
    tempC: number | null;
    apparentTempC: number | null;
    humidity: number | null;
    rainNow: number | null;
    windKmh: number | null;
    windDeg: number | null;
    uv: number | null;
    cloudPct: number | null;
    pressurehPa: number | null;
    visKm: number | null;
    isDay: boolean | null;
    condition: string;
    sunrise: string | null;
    sunset: string | null;
    daily: Array<{ date: string; tempMaxC: number; tempMinC: number; precipMm: number; precipProb: number }>;
  } | null;
  hostPulse: {
    iticEvents: number | null;
    openReports: number | null;
    news24h: number;
    shuttleLive: number | null;
  };
  precipNowcast: PrecipNowcast | null;
}


const fmtTemp = (t: number | null) => (t == null ? "—" : `${Math.round(t)}°`);
const fmtPct = (p: number | null) => (p == null ? "—" : `${Math.round(p)}%`);
const fmtInt = (n: number | null) => (n == null ? "—" : String(Math.round(n)));
const fmtFix = (n: number | null, d = 1) => (n == null ? "—" : n.toFixed(d));

function dayLabel(iso: string, hostTz = "Asia/Bangkok"): string {
  const d = new Date(iso + "T00:00:00");
  return new Intl.DateTimeFormat("en-US", { timeZone: hostTz, weekday: "short" }).format(d).toUpperCase();
}

export function WorldStrip({ hostAqi, hostPm25, hostWeather, hostPulse, precipNowcast, hostAqiScale = "us-epa", hostAirKind = "model", hostAirDetail }: Props) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const { clocks, setAt, slots } = useCustomClocks();
  const [editing, setEditing] = useState<number | null>(null);

  const band = hostAqi != null ? evidenceAqiBand(hostAqi, hostAqiScale) : null;
  const aqi = { label: band?.en ?? "Unknown", color: band?.level === "good" ? "var(--good)" : band?.level === "watch" ? "var(--warn)" : band?.level === "alert" ? "var(--bad)" : band?.level === "critical" ? "var(--crit)" : "var(--text-3)" };
  const uv = uvBand(hostWeather?.uv ?? null);
  const rain = rainBadge(precipNowcast);
  const wind = hostWeather?.windKmh ?? null;
  const windDir = windDirLabel(hostWeather?.windDeg ?? null);
  const sunrise = hmFromIso(hostWeather?.sunrise ?? null);
  const sunset = hmFromIso(hostWeather?.sunset ?? null);
  const sunLabel = hostWeather?.isDay === false ? "SUNRISE" : "SUNSET";
  const sunTime = hostWeather?.isDay === false ? sunrise : sunset;
  const hostTime = timeInTz("Asia/Bangkok", now);

  return (
    <div className="world-strip">
      <section className="world-host">
        <div className="world-host-head">
          <span className="eyebrow mono">Chonburi · host</span>
          <span className="mono caption">
            {hostTime.hms} · {hostTime.offset}
          </span>
        </div>
        <div className="world-host-row">
          <div className="world-host-now">
            <span className="world-host-temp">{fmtTemp(hostWeather?.tempC ?? null)}</span>
            <span className="world-host-cond">{hostWeather?.condition ?? "—"}</span>
            <span className="world-host-feels mono">
              FL {fmtTemp(hostWeather?.apparentTempC ?? null)}
            </span>
          </div>
          <div className="world-host-stats">
            <div className="world-stat">
              <span className="lbl">HUMIDITY</span>
              <span className="val mono">{fmtPct(hostWeather?.humidity ?? null)}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">WIND</span>
              <span className="val mono">{wind != null ? `${Math.round(wind)}` : "—"}</span>
              <span className="sub mono">{wind != null ? `KM/H ${windDir}` : "—"}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">UV</span>
              <span className="val mono" style={{ color: uv.color }}>{fmtFix(hostWeather?.uv ?? null, 1)}</span>
              <span className="sub mono" style={{ color: uv.color }}>{uv.label}</span>
            </div>
            <div className="world-stat">
              <span className="lbl" title={hostAirDetail}>{hostAqiScale === "thai-pcd" ? "PCD AQI" : "US AQI"}</span>
              <span className="val mono" title={`${hostAirKind === "model" ? "Forecast model" : "Station observation"} · ${hostAirDetail ?? ""}`} style={{ color: aqi.color }}>{hostAqi ?? "—"}</span>
              <span className="sub mono" style={{ color: aqi.color }}>
                {hostPm25 != null ? `PM2.5 ${hostPm25.toFixed(1)}` : aqi.label}
              </span>
            </div>
            <div className="world-stat">
              <span className="lbl">NOWCAST</span>
              <span className="val mono" style={{ color: rain.color }}>{rain.label}</span>
              <span className="sub mono" style={{ color: rain.color }}>{rain.sub}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">Rain</span>
              <span className="val mono">{fmtFix(hostWeather?.rainNow ?? null, 1)}</span>
              <span className="sub mono"><span className="world-rain-unit">mm/15 min model</span><span className="world-rain-cloud"> · cloud {fmtPct(hostWeather?.cloudPct ?? null)}</span></span>
            </div>
            <div className="world-stat">
              <span className="lbl">VIS</span>
              <span className="val mono">{fmtFix(hostWeather?.visKm ?? null, 1)}</span>
              <span className="sub mono">
                KM · {fmtInt(hostWeather?.pressurehPa ?? null)} hPa
              </span>
            </div>
            <div className="world-stat">
              <span className="lbl">{sunLabel}</span>
              <span className="val mono">{sunTime}</span>
              <span className="sub mono">↑ {sunrise} · ↓ {sunset}</span>
            </div>
          </div>
          <div className="world-host-pulse" aria-label="Chonburi live operational pulse">
            <div className="pulse-head">
              <span className="dot live" />
              <span className="eyebrow mono">Chonburi pulse</span>
            </div>
            <div className="pulse-grid">
              <div className="pulse-cell">
                <span className="lbl">iTIC EVT</span>
                <span
                  className="val mono"
                  style={{ color: pulseColor(hostPulse.iticEvents, 5, 15) }}
                >
                  {hostPulse.iticEvents ?? "—"}
                </span>
              </div>
              <div className="pulse-cell">
                <span className="lbl">CR OPEN</span>
                <span
                  className="val mono"
                  style={{ color: pulseColor(hostPulse.openReports, 6, 21) }}
                >
                  {hostPulse.openReports ?? "—"}
                </span>
              </div>
              <div className="pulse-cell">
                <span className="lbl">NEWS 24H</span>
                <span className="val mono">{hostPulse.news24h}</span>
              </div>
              <div className="pulse-cell">
                <span className="lbl">MUNI BUS</span>
                <span className="val mono">{hostPulse.shuttleLive ?? "—"}</span>
              </div>
            </div>
          </div>
          <div className="world-forecast" aria-label="5-day rain probability">
            {(hostWeather?.daily ?? []).slice(0, 5).map((d, i) => (
              <div className="world-day" key={d.date}>
                <span className="world-day-name mono">{i === 0 ? "TODAY" : dayLabel(d.date)}</span>
                <span className="world-day-bar" title={`${d.precipProb}% rain · ${d.precipMm.toFixed(1)}mm`}>
                  <span
                    className="world-day-fill"
                    style={{
                      height: `${Math.max(6, Math.round(d.precipProb))}%`,
                      background:
                        d.precipProb >= 70 ? "var(--bad)"
                          : d.precipProb >= 40 ? "var(--warn)"
                            : "var(--data)",
                    }}
                  />
                </span>
                <span className="world-day-pct mono">{Math.round(d.precipProb)}%</span>
                <span className="world-day-temp mono">
                  {Math.round(d.tempMinC)}–{Math.round(d.tempMaxC)}°
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3 user-editable clock slots ── */}
      <section className="world-partners">
        {Array.from({ length: slots }, (_, idx) => {
          const c = clocks[idx];
          return c ? (
            <button
              type="button"
              className="world-city world-city-filled"
              key={c.id + idx}
              onClick={() => setEditing(idx)}
              title="Click to change this clock"
            >
              <span className="world-city-name">{c.label}</span>
              <span className="world-city-time mono">{timeInTz(c.tz, now).hm}</span>
              <span className="world-city-meta mono">
                {c.country} · {timeInTz(c.tz, now).offset.replace("GMT", "UTC")}
              </span>
              <span className="world-city-tz mono">{c.tz}</span>
            </button>
          ) : (
            <button
              type="button"
              className="world-city world-city-empty"
              key={`empty-${idx}`}
              onClick={() => setEditing(idx)}
              aria-label="Add a clock"
            >
              <span className="world-city-plus" aria-hidden>+</span>
              <span className="world-city-empty-label mono">ADD CLOCK</span>
            </button>
          );
        })}
      </section>

      {editing !== null && (
        <ClockPicker
          existing={clocks[editing]}
          onClose={() => setEditing(null)}
          onPick={(spec) => {
            setAt(editing, spec);
            setEditing(null);
          }}
          onClear={() => {
            setAt(editing, null);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// ── City picker overlay ─────────────────────────────────────────────────

interface ClockPickerProps {
  existing: ClockSpec | null;
  onClose: () => void;
  onPick: (spec: ClockSpec) => void;
  onClear: () => void;
}

function ClockPicker({ existing, onClose, onPick, onClear }: ClockPickerProps) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ClockSpec[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounce = useRef<number | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    const ctrl = new AbortController();
    if (debounce.current) window.clearTimeout(debounce.current);
    if (q.trim().length < 2) { setResults([]); return; }
    debounce.current = window.setTimeout(async () => {
      setLoading(true);
      const r = await searchCities(q, ctrl.signal);
      if (!active) return;
      setResults(r);
      setLoading(false);
    }, 220);
    return () => {
      active = false;
      ctrl.abort();
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [q]);

  const hint = useMemo(() => {
    if (loading) return "Searching…";
    if (q.length < 2) return "Try \"Munich\", \"Hong Kong\", \"Berkeley\", \"Boston\"…";
    if (results.length === 0) return `No matches for "${q}"`;
    return null;
  }, [q, loading, results.length]);

  return (
    <div className="clock-picker-backdrop" onClick={onClose}>
      <div className="clock-picker" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Choose a city">
        <header className="clock-picker-head">
          <div>
            <span className="eyebrow mono">Add city clock</span>
            {existing && <div className="clock-picker-cur">{existing.label} · {existing.tz}</div>}
          </div>
          <button onClick={onClose} className="mono clock-picker-close" aria-label="Close">ESC</button>
        </header>
        <input
          ref={inputRef}
          type="search"
          className="clock-picker-input mono"
          placeholder="Search any city…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        {hint && <div className="clock-picker-hint caption">{hint}</div>}
        {results.length > 0 && (
          <ul className="clock-picker-results">
            {results.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => onPick(r)} className="clock-picker-row">
                  <span className="clock-picker-name">{r.label}</span>
                  <span className="clock-picker-meta mono">
                    {r.country} · {r.tz}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {existing && (
          <button
            type="button"
            onClick={onClear}
            className="clock-picker-clear mono"
            aria-label="Remove this clock"
          >
            REMOVE CLOCK
          </button>
        )}
      </div>
    </div>
  );
}
